
import type { DentalOffice } from '../../types';

interface SubscriptionInfoProps {
  office: DentalOffice;
  onUpdate: (data: { subscription_tier?: string; billing_cycle?: string }) => void;
  canManage: boolean;
}

const tierFeatures = {
  starter: [
    'Allt að 50 sjúklingar',
    'Tímabókanir og áminnningar',
    'Grunnmeðferðir',
    'Reikningar og greiðslur',
    '1 notandi',
  ],
  professional: [
    'Allt að 500 sjúklingar',
    'Tímabókanir og áminnningar',
    'Allar meðferðir og myndir',
    'Reikningar og greiðslur',
    'Allt að 5 notendur',
    'MyHealth samþætting',
  ],
  enterprise: [
    'Ótakmarkaðir sjúklingar',
    'Tímabókanir og áminnningar',
    'Allar meðferðir og myndir',
    'Reikningar og greiðslur',
    'Ótakmarkaðir notendur',
    'MyHealth samþætting',
    'Forgangsþjónusta',
    'Sérsniðnar skýrslur',
  ],
};

const tierLabels: Record<string, string> = {
  starter: 'Byrjandi',
  professional: 'Faglegur',
  enterprise: 'Fyrirtæki',
};

const statusLabels: Record<string, { label: string; color: string }> = {
  active: { label: 'Virk', color: 'bg-green-100 text-green-800' },
  trial: { label: 'Prufutími', color: 'bg-blue-100 text-blue-800' },
  suspended: { label: 'Í bið', color: 'bg-red-100 text-red-800' },
};

export function SubscriptionInfo({ office, onUpdate, canManage }: SubscriptionInfoProps) {
  const features = tierFeatures[office.subscription_tier] || [];
  const tierLabel = tierLabels[office.subscription_tier] || office.subscription_tier;
  const status = statusLabels[office.subscription_status] || {
    label: office.subscription_status,
    color: 'bg-gray-100 text-gray-800',
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('is-IS', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {tierLabel}
            </h3>
            <div className="flex gap-2">
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                {status.label}
              </span>
              {office.trial_ends_at && office.subscription_status === 'trial' && (
                <span className="text-sm text-gray-600">
                  Prufutími rennur út: {formatDate(office.trial_ends_at)}
                </span>
              )}
            </div>
          </div>

          {canManage && (
            <div className="flex gap-2">
              {office.subscription_tier !== 'professional' && (
                <button
                  onClick={() => onUpdate({ subscription_tier: 'professional' })}
                  className="px-4 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-sm"
                >
                  Uppfæra
                </button>
              )}
              {office.subscription_tier === 'enterprise' && (
                <button
                  onClick={() => onUpdate({ subscription_tier: 'professional' })}
                  className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm"
                >
                  Lækka
                </button>
              )}
            </div>
          )}
        </div>

        {/* Features */}
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Þjónusta:</h4>
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center text-sm text-gray-600">
                <svg
                  className="w-5 h-5 text-green-500 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Available Plans */}
      {canManage && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Aðrar áskriftir</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(tierFeatures).map(([tier, features]) => {
              if (tier === office.subscription_tier) return null;
              return (
                <div
                  key={tier}
                  className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {tierLabels[tier]}
                  </h4>
                  <ul className="space-y-1 mb-4">
                    {features.slice(0, 3).map((feature, index) => (
                      <li key={index} className="flex items-center text-xs text-gray-600">
                        <svg
                          className="w-4 h-4 text-green-500 mr-1 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => onUpdate({ subscription_tier: tier })}
                    className="w-full px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    Velja {tierLabels[tier]}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
